import React, { useEffect, useRef, useState } from "react";
import { useGesture } from "@use-gesture/react";
import gsap from "gsap";
import "./MagneticCursor.css";

const MagneticCursor = ({ theme }) => {
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const magneticAreaRef = useRef(null);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorEnlarged, setCursorEnlarged] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);
  const previousPathRef = useRef(window.location.pathname);

  // Initialize cursor position state
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Special handling for theme toggle button
  useEffect(() => {
    const themeToggleButton = document.querySelector(
      '[aria-label="Toggle Theme"]',
    );
    if (themeToggleButton) {
      const handleThemeToggleClick = () => {
        // Immediately reset cursor size
        setCursorEnlarged(false);
        setHoveredElement(null);

        gsap.to(cursorRef.current, {
          duration: 0.1,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.1,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });
      };

      themeToggleButton.addEventListener("click", handleThemeToggleClick);
      return () => {
        themeToggleButton.removeEventListener("click", handleThemeToggleClick);
      };
    }
  }, []);

  // Monitor for path changes without using Router hooks
  useEffect(() => {
    const checkForPathChange = () => {
      const currentPath = window.location.pathname;
      if (previousPathRef.current !== currentPath) {
        // Path has changed - reset cursor
        setCursorEnlarged(false);
        setHoveredElement(null);

        // Reset cursor visual state
        if (cursorRef.current && followerRef.current) {
          gsap.to(cursorRef.current, {
            duration: 0.2,
            scale: 1,
            opacity: 1, // Ensure cursor stays visible
          });

          gsap.to(followerRef.current, {
            duration: 0.2,
            scale: 1,
            opacity: 1, // Ensure cursor stays visible
          });
        }

        previousPathRef.current = currentPath;
      }
    };

    // Check periodically for URL changes
    const interval = setInterval(checkForPathChange, 100);

    return () => clearInterval(interval);
  }, []);

  // Set up magnetic effect for interactive elements
  useEffect(() => {
    // Function to set up event handlers on elements
    const setupMagneticElements = () => {
      // Elements that should have magnetic effect
      const magneticElements = document.querySelectorAll(
        "a, button, .interactive, .software-skill-inline",
      );

      // Handler functions that correctly use the current element
      const handleMouseEnter = (e) => {
        // Special case for theme toggle - use smaller enlargement
        if (
          e.currentTarget.getAttribute("aria-label") === "Toggle Theme" ||
          e.currentTarget.parentElement?.getAttribute("aria-label") ===
            "Toggle Theme"
        ) {
          gsap.to(cursorRef.current, {
            duration: 0.2,
            scale: 1.1,
            overwrite: true,
            opacity: 1, // Ensure cursor stays visible
          });

          gsap.to(followerRef.current, {
            duration: 0.2,
            scale: 1.3,
            overwrite: true,
            opacity: 1, // Ensure cursor stays visible
          });
        } else {
          // Use more modest enlargement for all other interactive elements
          gsap.to(cursorRef.current, {
            duration: 0.2,
            scale: 1.15, // Reduced from default 1.3
            overwrite: true,
            opacity: 1,
          });

          gsap.to(followerRef.current, {
            duration: 0.2,
            scale: 1.3, // Reduced from default 1.8
            overwrite: true,
            opacity: 1,
          });

          setCursorEnlarged(true);
        }

        setHoveredElement(e.currentTarget);
      };

      const handleMouseLeave = () => {
        setCursorEnlarged(false);
        setHoveredElement(null);

        // Force reset cursor size on mouse leave
        gsap.to(cursorRef.current, {
          duration: 0.2,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.2,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });
      };

      // Apply magnetic effect to each element
      magneticElements.forEach((el) => {
        el.addEventListener("mouseenter", handleMouseEnter);
        el.addEventListener("mouseleave", handleMouseLeave);

        // Add special handling for skills and theme toggle button
        if (el.classList.contains("software-skill-inline")) {
          el.addEventListener("click", () => {
            // Force reset the cursor size after clicking skills
            setCursorEnlarged(false);
            setHoveredElement(null);

            gsap.to(cursorRef.current, {
              duration: 0.1,
              scale: 1,
              overwrite: true,
              opacity: 1, // Ensure cursor stays visible
            });

            gsap.to(followerRef.current, {
              duration: 0.1,
              scale: 1,
              overwrite: true,
              opacity: 1, // Ensure cursor stays visible
            });
          });
        }
      });

      // Clean up function
      return () => {
        magneticElements.forEach((el) => {
          el.removeEventListener("mouseenter", handleMouseEnter);
          el.removeEventListener("mouseleave", handleMouseLeave);

          if (el.classList.contains("software-skill-inline")) {
            el.removeEventListener("click", () => {});
          }
        });
      };
    };

    // Initial setup
    const cleanup = setupMagneticElements();

    // Set up a mutation observer to handle dynamically added elements
    const observer = new MutationObserver(() => {
      cleanup(); // Remove old event listeners
      setupMagneticElements(); // Setup new ones
    });

    // Start observing the document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Clean up everything when component unmounts
    return () => {
      cleanup();
      observer.disconnect();
    };
  }, []);

  // Handle mouse movement
  useEffect(() => {
    const onMouseMove = (e) => {
      const { clientX: x, clientY: y } = e;
      setPosition({ x, y });
      setCursorVisible(true);

      if (hoveredElement) {
        // Get element bounds
        const { left, top, width, height } =
          hoveredElement.getBoundingClientRect();
        const centerX = left + width / 2;
        const centerY = top + height / 2;

        // Calculate distance from cursor to element center
        const deltaX = x - centerX;
        const deltaY = y - centerY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Apply magnetic effect if cursor is close enough
        if (distance < 100) {
          // Move the cursor towards the element with magnetic force
          const magneticForce = Math.min(1, (100 - distance) / 100) * 0.4; // Increased magnetic force
          const newX = x - deltaX * magneticForce;
          const newY = y - deltaY * magneticForce;

          gsap.to(cursorRef.current, {
            duration: 0.05, // Faster main cursor (was 0.3)
            x: newX,
            y: newY,
            opacity: 1, // Ensure cursor stays visible
          });

          gsap.to(followerRef.current, {
            duration: 0.2, // Faster follower cursor (was 0.5)
            x: newX,
            y: newY,
            opacity: 1, // Ensure cursor stays visible
          });
        } else {
          // Normal cursor movement
          gsap.to(cursorRef.current, {
            duration: 0.05, // Faster main cursor (was 0.1)
            x,
            y,
            opacity: 1, // Ensure cursor stays visible
          });

          gsap.to(followerRef.current, {
            duration: 0.5, // Faster follower cursor (was 0.5)
            x,
            y,
            opacity: 1, // Ensure cursor stays visible
          });
        }
      } else {
        // Normal cursor movement
        gsap.to(cursorRef.current, {
          duration: 0.01, // Faster main cursor (was 0.1)
          x,
          y,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.15, // Faster follower cursor (was 0.5)
          x,
          y,
          opacity: 1, // Ensure cursor stays visible
        });
      }
    };

    const onMouseLeave = () => {
      setCursorVisible(false);
    };

    const onMouseEnter = () => {
      setCursorVisible(true);
      gsap.to([cursorRef.current, followerRef.current], {
        duration: 0.2,
        opacity: 1,
        overwrite: true,
      });
    };

    const onMouseDown = () => {
      // Special case for theme toggle
      if (
        hoveredElement &&
        (hoveredElement.getAttribute("aria-label") === "Toggle Theme" ||
          hoveredElement.parentElement?.getAttribute("aria-label") ===
            "Toggle Theme")
      ) {
        gsap.to(cursorRef.current, {
          duration: 0.1,
          scale: 0.9,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.1,
          scale: 1.1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });
      } else {
        gsap.to(cursorRef.current, {
          duration: 0.2,
          scale: 0.7,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.2,
          scale: 1.4,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });
      }
    };

    const onMouseUp = () => {
      // Special case for theme toggle
      if (
        hoveredElement &&
        (hoveredElement.getAttribute("aria-label") === "Toggle Theme" ||
          hoveredElement.parentElement?.getAttribute("aria-label") ===
            "Toggle Theme")
      ) {
        // Reset immediately for theme toggle
        gsap.to(cursorRef.current, {
          duration: 0.1,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.1,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        // Clear states
        setCursorEnlarged(false);
        setHoveredElement(null);
      }
      // Only enlarge if still hovering an element, otherwise reset to normal size
      else if (hoveredElement && document.body.contains(hoveredElement)) {
        // Element is still in DOM and being hovered
        gsap.to(cursorRef.current, {
          duration: 0.2,
          scale: cursorEnlarged ? 1.15 : 1, // Reduced from 1.3
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.2,
          scale: cursorEnlarged ? 1.3 : 1, // Reduced from 1.8
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });
      } else {
        // Reset to normal size
        gsap.to(cursorRef.current, {
          duration: 0.2,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        gsap.to(followerRef.current, {
          duration: 0.2,
          scale: 1,
          overwrite: true,
          opacity: 1, // Ensure cursor stays visible
        });

        setCursorEnlarged(false);
        setHoveredElement(null);
      }
    };

    // Listen for click events on navigation links to reset cursor state
    const handleLinkClick = () => {
      setCursorEnlarged(false);
      setHoveredElement(null);

      // Reset cursor scales
      gsap.to(cursorRef.current, {
        duration: 0.2,
        scale: 1,
        overwrite: true,
        opacity: 1, // Ensure cursor stays visible
      });

      gsap.to(followerRef.current, {
        duration: 0.2,
        scale: 1,
        overwrite: true,
        opacity: 1, // Ensure cursor stays visible
      });
    };

    const navLinks = document.querySelectorAll("nav a, header a");
    navLinks.forEach((link) => {
      link.addEventListener("click", handleLinkClick);
    });

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("mouseenter", onMouseEnter);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("mouseenter", onMouseEnter);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mouseup", onMouseUp);

      navLinks.forEach((link) => {
        link.removeEventListener("click", handleLinkClick);
      });
    };
  }, [cursorEnlarged, hoveredElement]);

  // Initial setup of cursor
  useEffect(() => {
    // Hide native cursor
    document.body.style.cursor = "none";

    // Initialize cursor position
    gsap.set(cursorRef.current, {
      x: position.x,
      y: position.y,
      opacity: 0,
      backgroundColor: "#645beb", // Updated to match CSS
    });

    gsap.set(followerRef.current, {
      x: position.x,
      y: position.y,
      opacity: 0,
      borderColor: "#645beb", // Updated to match CSS
    });

    // Show cursor with animation
    gsap.to([cursorRef.current, followerRef.current], {
      duration: 0.5,
      opacity: 1,
      delay: 0.5,
    });

    return () => {
      // Restore native cursor
      document.body.style.cursor = "auto";
    };
  }, [position.x, position.y]);

  // Force update cursor color when theme changes
  useEffect(() => {
    if (cursorRef.current && followerRef.current) {
      gsap.to(cursorRef.current, {
        backgroundColor: "#8892b0",
        duration: 0.3,
        opacity: 1, // Ensure cursor stays visible
      });

      gsap.to(followerRef.current, {
        borderColor: "#8892b0",
        duration: 0.3,
        opacity: 1, // Ensure cursor stays visible
      });
    }
  }, [theme]);

  return (
    <>
      <div
        ref={cursorRef}
        className={`magnetic-cursor ${cursorEnlarged ? "enlarged" : ""} ${
          !cursorVisible ? "hidden" : ""
        }`}
        style={{ backgroundColor: "#8892b0" }}
      />
      <div
        ref={followerRef}
        className={`magnetic-cursor-follower ${
          cursorEnlarged ? "enlarged" : ""
        } ${!cursorVisible ? "hidden" : ""}`}
        style={{ borderColor: "#8892b0" }}
      />
      <div ref={magneticAreaRef} className="magnetic-area" />
    </>
  );
};

export default MagneticCursor;
